import React, { useState, useEffect } from 'react'
import { useCourses } from '@/hooks/useCourses'
import { affiliateService } from '@/services/affiliateService'
import { courseService } from '@/services/courseService'
import { CourseCommissionModal } from './CourseCommissionModal'
import type { AffiliateCourseCommission, Affiliate } from '@/types/affiliate'
import type { Course } from '@/types/course'

interface AffiliateCourseCommissionPanelProps {
    mode: 'by-affiliate' | 'by-course'
    affiliateId?: string
    courseId?: string
    commissions: AffiliateCourseCommission[]
    onRefresh: () => void
}

export const AffiliateCourseCommissionPanel: React.FC<AffiliateCourseCommissionPanelProps> = ({
    mode,
    affiliateId,
    courseId,
    commissions,
    onRefresh,
}) => {
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [editingCommission, setEditingCommission] = useState<AffiliateCourseCommission | null>(null)
    const [editingDefault, setEditingDefault] = useState(false)
    const [deletingId, setDeletingId] = useState<string | null>(null)
    const [affiliates, setAffiliates] = useState<Affiliate[]>([])
    const [currentCourse, setCurrentCourse] = useState<Course | null>(null)

    const { data: courses } = useCourses()

    // Fetch all affiliates from API
    useEffect(() => {
        const fetchAffiliates = async () => {
            try {
                const data = await affiliateService.getAllAffiliates()
                setAffiliates(data)
            } catch (error) {
                console.error('Failed to fetch affiliates:', error)
                setAffiliates([])
            }
        }

        if (mode === 'by-course') {
            fetchAffiliates()
        }
    }, [mode])

    // Fetch course data when in by-course mode to get default commission
    useEffect(() => {
        const fetchCourse = async () => {
            if (!courseId) return
            try {
                const response = await courseService.getCourseById(courseId)
                setCurrentCourse(response.data || null)
            } catch (error) {
                console.error('Failed to fetch course:', error)
                setCurrentCourse(null)
            }
        }

        if (mode === 'by-course' && courseId) {
            fetchCourse()
        }
    }, [mode, courseId])

    const handleSubmit = async (data: {
        affiliateId: string
        courseId: string
        commissionType: 'percentage' | 'fixed'
        commissionValue: number
    }) => {
        // Check if this is a default commission (ALL AFFILIATES)
        if (data.affiliateId === 'ALL') {
            await courseService.setDefaultCommission(
                data.courseId,
                data.commissionType,
                data.commissionValue
            )
            // Refresh course data to show updated default commission
            const response = await courseService.getCourseById(data.courseId)
            setCurrentCourse(response.data || null)
        } else {
            await affiliateService.setCourseCommission(
                data.affiliateId,
                data.courseId,
                data.commissionType,
                data.commissionValue
            )
        }
        onRefresh()
    }

    const handleDelete = async (affId: string, crsId: string) => {
        if (!window.confirm('Are you sure you want to delete this commission setting?')) {
            return
        }

        setDeletingId(`${affId}-${crsId}`)
        try {
            // Check if this is a default commission
            if (affId === 'ALL') {
                await courseService.removeDefaultCommission(crsId)
                // Refresh course data to show removal
                const response = await courseService.getCourseById(crsId)
                setCurrentCourse(response.data || null)
            } else {
                await affiliateService.deleteCourseCommission(affId, crsId)
            }
            onRefresh()
        } catch (error) {
            console.error('Failed to delete commission:', error)
            alert('Failed to delete commission')
        } finally {
            setDeletingId(null)
        }
    }

    const getCourseName = (cId: string) => {
        const course = courses?.find((c: Course) => c.id === cId)
        return course?.title || 'Unknown Course'
    }

    const getAffiliateCode = (affId: string) => {
        const affiliate = affiliates.find((a) => a.id === affId)
        return affiliate?.code || 'Unknown Affiliate'
    }

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                        {mode === 'by-affiliate' ? 'Course Commissions' : 'Affiliate Commissions'}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-text-muted">
                        {mode === 'by-affiliate'
                            ? 'Set custom commission rates for specific courses'
                            : 'Set custom commission rates for specific affiliates'}
                    </p>
                </div>
                <button
                    onClick={() => {
                        setEditingCommission(null)
                        setIsModalOpen(true)
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg font-bold text-sm hover:scale-105 transition-transform"
                >
                    <span className="material-symbols-outlined text-[18px]">add</span>
                    Add Commission
                </button>
            </div>

            {commissions.length === 0 && (!currentCourse?.defaultCommissionType || mode !== 'by-course') ? (
                <div className="rounded-lg bg-white dark:bg-surface-card border border-gray-200 dark:border-white/5 p-12 text-center">
                    <div className="size-16 rounded-full bg-gray-100 dark:bg-white/5 mx-auto mb-4 flex items-center justify-center">
                        <span className="material-symbols-outlined text-[32px] text-gray-500">
                            payments
                        </span>
                    </div>
                    <p className="text-gray-900 dark:text-white font-bold mb-2">No custom commissions</p>
                    <p className="text-sm text-gray-500 dark:text-text-muted mb-4">
                        {mode === 'by-affiliate'
                            ? 'This affiliate uses global commission rate for all courses'
                            : 'This course uses global commission rate for all affiliates'}
                    </p>
                    <button
                        onClick={() => {
                            setEditingCommission(null)
                            setEditingDefault(false)
                            setIsModalOpen(true)
                        }}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-white/5 text-gray-900 dark:text-white rounded-lg font-medium hover:bg-gray-200 dark:hover:bg-white/10 transition-colors"
                    >
                        <span className="material-symbols-outlined text-[18px]">add</span>
                        Add First Commission
                    </button>
                </div>
            ) : (
                <div className="rounded-lg bg-white dark:bg-surface-card border border-gray-200 dark:border-white/5 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[500px]">
                            <thead>
                                <tr className="border-b border-gray-200 dark:border-white/5">
                                    <th className="text-left px-5 py-4 text-xs font-bold uppercase text-gray-500 dark:text-text-muted">
                                        {mode === 'by-affiliate' ? 'Course' : 'Affiliate'}
                                    </th>
                                    <th className="text-left px-5 py-4 text-xs font-bold uppercase text-gray-500 dark:text-text-muted">
                                        Type
                                    </th>
                                    <th className="text-left px-5 py-4 text-xs font-bold uppercase text-gray-500 dark:text-text-muted">
                                        Value
                                    </th>
                                    <th className="text-right px-5 py-4 text-xs font-bold uppercase text-gray-500 dark:text-text-muted">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-white/5">
                                {/* Default Commission Row (ALL AFFILIATES) - Only in by-course mode */}
                                {mode === 'by-course' && currentCourse?.defaultCommissionType && currentCourse?.defaultCommissionValue && (
                                    <tr className="bg-primary/5 hover:bg-primary/10 transition-colors border-l-4 border-primary">
                                        <td className="px-5 py-4">
                                            <div className="flex items-center gap-2">
                                                <span className="material-symbols-outlined text-primary text-[16px]">groups</span>
                                                <p className="text-sm font-bold text-gray-900 dark:text-white">ALL AFFILIATES (Default)</p>
                                            </div>
                                        </td>
                                        <td className="px-5 py-4">
                                            <span className={`inline-flex px-2.5 py-1 rounded-full text-[10px] font-bold uppercase ${
                                                currentCourse.defaultCommissionType === 'percentage'
                                                    ? 'bg-blue-500/20 text-blue-400'
                                                    : 'bg-green-500/20 text-green-400'
                                            }`}>
                                                {currentCourse.defaultCommissionType}
                                            </span>
                                        </td>
                                        <td className="px-5 py-4 text-sm text-gray-900 dark:text-white font-bold">
                                            {currentCourse.defaultCommissionType === 'percentage'
                                                ? `${currentCourse.defaultCommissionValue}%`
                                                : `Rp ${currentCourse.defaultCommissionValue.toLocaleString('id-ID')}`}
                                        </td>
                                        <td className="px-5 py-4">
                                            <div className="flex items-center justify-end gap-2">
                                                <button
                                                    onClick={() => {
                                                        setEditingDefault(true)
                                                        setEditingCommission(null)
                                                        setIsModalOpen(true)
                                                    }}
                                                    className="size-8 rounded-lg bg-gray-100 dark:bg-white/5 flex items-center justify-center hover:bg-gray-200 dark:hover:bg-white/10 transition-colors"
                                                    title="Edit"
                                                >
                                                    <span className="material-symbols-outlined text-[16px]">edit</span>
                                                </button>
                                                <button
                                                    onClick={() => courseId && handleDelete('ALL', courseId)}
                                                    disabled={deletingId === `ALL-${courseId}`}
                                                    className="size-8 rounded-lg bg-gray-100 dark:bg-white/5 flex items-center justify-center hover:bg-red-500/20 hover:text-red-400 transition-colors disabled:opacity-50"
                                                    title="Delete"
                                                >
                                                    <span className="material-symbols-outlined text-[16px]">
                                                        {deletingId === `ALL-${courseId}` ? 'hourglass_empty' : 'delete'}
                                                    </span>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                                {commissions.map((commission) => {
                                    const isDeleting = deletingId === `${commission.affiliateId}-${commission.courseId}`

                                    return (
                                        <tr key={commission.id} className="hover:bg-gray-100 dark:hover:bg-white/5 transition-colors">
                                            <td className="px-5 py-4">
                                                <p className="text-sm font-medium text-gray-900 dark:text-white">
                                                    {mode === 'by-affiliate'
                                                        ? getCourseName(commission.courseId)
                                                        : getAffiliateCode(commission.affiliateId)}
                                                </p>
                                            </td>
                                            <td className="px-5 py-4">
                                                <span className={`inline-flex px-2.5 py-1 rounded-full text-[10px] font-bold uppercase ${
                                                    commission.commissionType === 'percentage'
                                                        ? 'bg-blue-500/20 text-blue-400'
                                                        : 'bg-green-500/20 text-green-400'
                                                }`}>
                                                    {commission.commissionType}
                                                </span>
                                            </td>
                                            <td className="px-5 py-4 text-sm text-gray-900 dark:text-white font-bold">
                                                {commission.commissionType === 'percentage'
                                                    ? `${commission.commissionValue}%`
                                                    : `Rp ${commission.commissionValue.toLocaleString('id-ID')}`}
                                            </td>
                                            <td className="px-5 py-4">
                                                <div className="flex items-center justify-end gap-2">
                                                    <button
                                                        onClick={() => {
                                                            setEditingCommission(commission)
                                                            setIsModalOpen(true)
                                                        }}
                                                        className="size-8 rounded-lg bg-gray-100 dark:bg-white/5 flex items-center justify-center hover:bg-gray-200 dark:hover:bg-white/10 transition-colors"
                                                        title="Edit"
                                                    >
                                                        <span className="material-symbols-outlined text-[16px]">edit</span>
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(commission.affiliateId, commission.courseId)}
                                                        disabled={isDeleting}
                                                        className="size-8 rounded-lg bg-gray-100 dark:bg-white/5 flex items-center justify-center hover:bg-red-500/20 hover:text-red-400 transition-colors disabled:opacity-50"
                                                        title="Delete"
                                                    >
                                                        <span className="material-symbols-outlined text-[16px]">
                                                            {isDeleting ? 'hourglass_empty' : 'delete'}
                                                        </span>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            <CourseCommissionModal
                isOpen={isModalOpen}
                onClose={() => {
                    setIsModalOpen(false)
                    setEditingCommission(null)
                    setEditingDefault(false)
                }}
                onSubmit={handleSubmit}
                mode={mode}
                affiliateId={mode === 'by-affiliate' ? affiliateId : undefined}
                courses={mode === 'by-affiliate' ? courses : undefined}
                courseId={mode === 'by-course' ? courseId : undefined}
                affiliates={mode === 'by-course' ? affiliates : undefined}
                editingCommission={editingCommission || undefined}
                editingDefault={editingDefault}
                currentCourse={currentCourse}
            />
        </div>
    )
}
